import React, { useEffect, useState } from "react";
import { Box, Button, Stack, TextField, Typography, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import Popup from "../../../components/Popup";
import cmmsMessages from "../../cmms/cmmsMessages";
import messages from "../messages";
import AxionControls from "../../../components/controls/AxionControls";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import { reopenWorkOrder } from "../../../fetchers/cmmsWorkOrder";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import { formatCmmsError } from "../../cmms/components/CmmsStates";
import {
  getAssetHierarchyPalette,
  getContainedButtonSx,
  getOutlinedButtonSx,
} from "../../cmms/assetHierarchyStyles";

const MIN_REASON_LENGTH = 10;

export default function ReopenDialog({ open, onClose, workOrderId, onSuccess }) {
  const theme = useTheme();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const intl = useIntl();
  const [companyKey] = useLocalStorage("companyKey", 1);
  const queryClient = useQueryClient();

  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReason("");
    setError("");
  }, [open]);

  const handleSubmit = async () => {
    if (reason.trim().length < MIN_REASON_LENGTH) {
      setError(`Reason must be at least ${MIN_REASON_LENGTH} characters`);
      return;
    }

    setSubmitting(true);
    try {
      // No userId: the reopen and the comment carrying the reason are both signed from the token.
      const result = await reopenWorkOrder(workOrderId, {
        companyKey,
        reason: reason.trim(),
      });
      pushNotification(intl.formatMessage(messages.toastWorkOrderReopened));
      await queryClient.invalidateQueries({ queryKey: ["cmmsWorkOrders"] });
      // Reopening puts the work order back into Open, so it re-enters /mine and the badge.
      await queryClient.invalidateQueries({ queryKey: ["cmmsMyWorkOrders"] });
      onSuccess?.(result);
      onClose?.();
    } catch (submitError) {
      pushErrorMessage(formatCmmsError(submitError, intl.formatMessage(messages.reopenFailed)));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Popup openPopup={open} setOpenPopup={(value) => { if (!value) onClose?.(); }} maxWidth="sm" title={intl.formatMessage(messages.reopenDialogTitle)}>
      <Box sx={{ display: "grid", gap: 2, p: 1 }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          <FormattedMessage {...messages.reopenIntro} />
        </Typography>

        <TextField
          label={intl.formatMessage(messages.reopenReason)}
          required
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          error={Boolean(error)}
          helperText={error}
          multiline
          rows={4}
          size="small"
          fullWidth
        />

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button variant="outlined" onClick={onClose} disabled={submitting} sx={getOutlinedButtonSx(cmmsColors)}><FormattedMessage {...cmmsMessages.cancel} /></Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting} sx={getContainedButtonSx(cmmsColors)}><FormattedMessage {...cmmsMessages.actionReopen} /></Button>
        </Stack>
      </Box>
      <AxionControls.AxionBackdrop open={submitting} />
    </Popup>
  );
}
